import { SelectV4, TextInputV4, Truncate } from '@labrav/react-components'
import { DataType } from '@labrav/react-components/lib/@types/types/forms'
import { Grid, SelectChangeEvent } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-regular-svg-icons'
import { FormikProps, useFormikContext } from 'formik'
import { cloneDeep, get } from 'lodash'
import { useCallback } from 'react'
import { getValidValueBetweenMinMax } from '@labrav/common'
import {
  AzureOfferPlan,
  AzurePricingType,
} from '../../../../modules/privateOffer/types'
import { Product } from '../../../../modules/productsListing/types'
import { useStyles } from '../Plan/PlanRow/PlanRow.styles'
import { useVmPlanRowStyles } from './VmPlanRow.styles'
import {
  convertVmProductPlanToOfferPlan,
  resolveVmPricingTypeForPlanSelection,
  updateVmPlanDiscount,
} from '../../../../components/MicrosoftFormComponent/VmPlan/vmPlanUtils'
import { VmPlanPricing } from './VmPlanPricing'

export const VmPlanRow = ({
  plan,
  index,
  product,
  planIds,
  remove,
}: {
  plan: AzureOfferPlan
  index: number
  product: Product
  planIds: string[]
  remove?: <X = unknown>(index: number) => X | undefined
}) => {
  const { classes } = useStyles()
  const { classes: vmPlanRowClasses } = useVmPlanRowStyles()
  const { values, touched, errors, handleBlur, setFieldValue } =
    useFormikContext() as FormikProps<Record<string, unknown>>

  const plans = (values?.plans || []) as AzureOfferPlan[]
  const formPlan = plans[index] || plan

  const azurePricingType = values?.azurePricingType
  const isDiscountPricing =
    azurePricingType === AzurePricingType.LABRA_PERCENTAGE

  const productPlans = product?.plans
    ?.filter(
      planItem =>
        planItem?.planId === plan?.labraPlanId ||
        !planIds.includes(planItem?.planId)
    )
    ?.filter(
      planItem =>
        !(
          planItem?.isPrivate ||
          planItem?.isStopSell ||
          planItem?.hasFreeTrials
        )
    )
    .map(planItem => ({
      label: planItem?.displayName,
      value: planItem?.planId,
    }))

  const handleChangePlanSelection = async (e: SelectChangeEvent) => {
    const selectedPlan = product?.plans?.find(
      item => item?.planId === e.target.value
    )
    if (selectedPlan) {
      const offerPlan = convertVmProductPlanToOfferPlan(selectedPlan)

      await setFieldValue(`plans.${[index]}`, {
        ...offerPlan,
        discountPercentage: null,
      })

      const nextPricingType = resolveVmPricingTypeForPlanSelection({
        productPlan: selectedPlan,
        currentPricingType: values.azurePricingType as AzurePricingType,
      })

      if (nextPricingType !== values.azurePricingType) {
        await setFieldValue('azurePricingType', nextPricingType)
      }
    }
  }

  const updateVmDiscount = useCallback(
    ({ discountPercentage }: { discountPercentage?: number }) => {
      const currentPlan =
        ((values.plans as AzureOfferPlan[])?.[index] as AzureOfferPlan) || plan
      const updatedPlan = updateVmPlanDiscount({
        plan: currentPlan,
        product,
        discountPercentage,
      })

      if (!updatedPlan) {
        return
      }

      const updatedPlans = [...(values.plans as AzureOfferPlan[])]
      updatedPlans[index] = cloneDeep(updatedPlan) as AzureOfferPlan
      setFieldValue('plans', [...cloneDeep(updatedPlans)])
    },
    [values, setFieldValue, index, plan, product]
  )

  const handleDiscountUpdate = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = getValidValueBetweenMinMax(
      event.target.value as string,
      1,
      100
    )
    await updateVmDiscount({
      discountPercentage: value || undefined,
    })
  }

  return (
    <div className={classes.planRowWrapper}>
      <div className={classes.header}>
        <div className={classes.plansHeading}>Plans</div>
        <div>
          {plans.length > 1 && (
            <FontAwesomeIcon
              icon={faTrash}
              className={vmPlanRowClasses.trashIcon}
              data-testid="trash-metaData"
              onClick={() => remove && remove(index)}
            />
          )}
        </div>
      </div>
      <Grid container width={'100%'} spacing={3}>
        <Grid size={4}>
          <div className={classes.planSelect}>
            <SelectV4
              field={{
                title: 'Select plan',
                placeHolder: 'Select plan',
                humanReadableKey: 'plan',
                id: `plans.${[index]}.labraPlanId`,
                isRequired: false,
                isReadOnly: false,
                isCustom: false,
                defaultValue: '',
                dataType: 'enum' as DataType,
                options: productPlans,
              }}
              size="small"
              value={plan?.labraPlanId || ''}
              onChange={handleChangePlanSelection}
              onBlur={handleBlur}
              isError={
                !!(
                  get(touched, `plans.${[index]}.labraPlanId`, false) &&
                  get(errors, `plans.${[index]}.labraPlanId`, false)
                )
              }
              errorText={
                (!!get(touched, `plans.${[index]}.labraPlanId`, false) &&
                  get(errors, `plans.${[index]}.labraPlanId`, '')) ||
                ''
              }
            />
          </div>
        </Grid>
        {plan?.labraPlanId && (
          <>
            <Grid size={isDiscountPricing ? 5 : 6}>
              <div className={classes.title}>Description</div>
              <div className={classes.content}>
                <Truncate showIcon={false} clipboardTooltip>
                  {plan.description}
                </Truncate>
              </div>
            </Grid>
            {isDiscountPricing && (
              <Grid size={3}>
                <div className={classes.content}>
                  <TextInputV4
                    keyName={`plans.${[index]}.discountPercentage`}
                    title="Discount (%)"
                    type="number"
                    placeHolder=""
                    alignRight
                    minNumber={1}
                    maxNumber={100}
                    value={formPlan?.discountPercentage ?? ''}
                    updatedValue={formPlan?.discountPercentage ?? ''}
                    isError={
                      !!(
                        get(
                          touched,
                          `plans.${[index]}.discountPercentage`,
                          false
                        ) &&
                        get(
                          errors,
                          `plans.${[index]}.discountPercentage`,
                          false
                        )
                      )
                    }
                    onChange={handleDiscountUpdate}
                    isReadOnly={false}
                    isRequired={false}
                    errorText={
                      (!!get(
                        touched,
                        `plans.${[index]}.discountPercentage`,
                        false
                      ) &&
                        get(
                          errors,
                          `plans.${[index]}.discountPercentage`,
                          ''
                        )) ||
                      ''
                    }
                    showSpinner={false}
                    onBlur={handleBlur}
                  />
                </div>
              </Grid>
            )}
          </>
        )}
      </Grid>
      {plan?.labraPlanId && (
        <VmPlanPricing index={index} plan={plan} product={product} />
      )}
    </div>
  )
}
